/**
 * Formatting Utilities
 * Common formatting functions for dates, currency, file sizes, etc.
 */

/**
 * Display labels for contract rate_type. The STORED value stays 'Variable'
 * everywhere (DB CHECK, stored procedures, all conditionals, existing rows);
 * only the text shown to the user changes. 'Fixed' and 'Floating' pass through
 * unchanged. Use formatRateType() at every site that prints rate_type.
 */
export const RATE_TYPE_LABELS = { Variable: 'Linked' };

/**
 * Map a stored rate_type value to its display label.
 * @param {string} value - stored rate_type ('Fixed' | 'Variable' | 'Floating')
 * @returns {string} display label ('Variable' -> 'Linked'; others unchanged)
 */
export const formatRateType = (value) => RATE_TYPE_LABELS[value] || value || '';

/**
 * Rate-change effective-basis constants + display labels (T3-068). Variable ("Linked") contracts
 * choose WHEN a benchmark rate change takes effect. The stored value stays these literals everywhere
 * (DB CHECK, stored procedures, all conditionals). This is the frontend's single definition and the
 * hand-synced twin of the API RATE_CHANGE_BASIS in capitalmanagement-api/utils/rateChangeBasis.js —
 * keep the two in sync (same as PermissionConstants.js <-> middleware/permissions.js).
 */
export const RATE_CHANGE_BASIS = Object.freeze({
  IMMEDIATE: 'immediate',
  ANNIVERSARY: 'anniversary',
  SPECIFIED_DATE: 'specified_date',
});

export const RATE_CHANGE_BASIS_LABELS = {
  [RATE_CHANGE_BASIS.IMMEDIATE]: 'On Date of Change',
  [RATE_CHANGE_BASIS.ANNIVERSARY]: 'On Annual Anniversary',
  [RATE_CHANGE_BASIS.SPECIFIED_DATE]: 'On Specified Date',
};

/**
 * Map a stored rate_change_effective_basis value to its display label.
 * @param {string} value - 'immediate' | 'anniversary' | 'specified_date'
 * @returns {string} display label (defaults to 'On Date of Change' for null/unknown)
 */
export const formatRateChangeBasis = (value) =>
  RATE_CHANGE_BASIS_LABELS[value] || RATE_CHANGE_BASIS_LABELS[RATE_CHANGE_BASIS.IMMEDIATE];

/**
 * Format a date value for display
 * @param {string|Date} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '-';

  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '-';

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options,
    };

    return new Intl.DateTimeFormat('en-ZA', defaultOptions).format(dateObj);
  } catch {
    return '-';
  }
};

/**
 * Format a date as a zero-padded short date: "08 Jul 2026".
 *
 * Differs from formatDate() which uses en-ZA `day:'numeric'` (non-padded, "8 Jul 2026").
 * This is the padded variant that matches the many local `format(d, 'dd MMM yyyy')` /
 * `toLocaleDateString('en-GB', { day:'2-digit', ... })` copies scattered across the app.
 *
 * Date-only strings (e.g. "2026-07-08") are normalised to local noon before parsing so a
 * UTC-midnight value never renders as the previous day in a behind-UTC timezone.
 *
 * @param {string|Date} date - Date to format (ISO string, "yyyy-MM-dd", or Date)
 * @param {object} [options]
 * @param {string} [options.fallback='-'] - Returned for null/empty/invalid input
 * @returns {string} e.g. "08 Jul 2026", or the fallback
 */
export const formatShortDate = (date, { fallback = '-' } = {}) => {
  if (!date) return fallback;
  try {
    // Normalise a plain date string to local noon to avoid TZ day-shift.
    const raw = typeof date === 'string' ? date.substring(0, 10) + 'T12:00:00' : date;
    const dateObj = new Date(raw);
    if (isNaN(dateObj.getTime())) return fallback;
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(dateObj);
  } catch {
    return fallback;
  }
};

/**
 * Format a date with time
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Deterministic numeric format: '.' decimal separator, space thousands separator.
// The en-ZA locale otherwise emits ',' as decimal which is visually confusable
// with the space thousands separator (e.g., "8 000,000" reads as eight million).
const formatNumberDeterministic = (numValue, decimals) => {
  const fixed = Math.abs(numValue).toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const body = decPart ? `${withThousands}.${decPart}` : withThousands;
  return numValue < 0 ? `-${body}` : body;
};

/**
 * Format a currency value
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (e.g., 'ZAR', 'USD')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'ZAR', decimals = null) => {
  if (amount === null || amount === undefined) return '-';

  try {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '-';

    const dp = decimals !== null ? decimals : 2;
    return `${currency || 'ZAR'} ${formatNumberDeterministic(numAmount, dp)}`;
  } catch {
    const dp = decimals !== null ? decimals : 2;
    return `${currency || 'ZAR'} ${parseFloat(amount || 0).toFixed(dp)}`;
  }
};

/**
 * Format a number with thousand separators
 * @param {number} value - Number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined) return '-';

  try {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '-';

    return formatNumberDeterministic(numValue, decimals);
  } catch {
    return value?.toString() || '-';
  }
};

/**
 * Format a percentage value
 * @param {number} value - Percentage value (e.g., 5.5 for 5.5%)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercent = (value, decimals = 2) => {
  if (value === null || value === undefined) return '-';

  try {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '-';

    return `${numValue.toFixed(decimals)}%`;
  } catch {
    return '-';
  }
};

/**
 * Format a fee/duty rate to up to 4 decimal places (the DB precision for
 * broker_fees / stamp_duty_fees / placement-fee percentage, DECIMAL(_,4)),
 * trimming trailing zeros so 1.5000 -> "1.5", 0.0125 -> "0.0125",
 * 1.2345 -> "1.2345". Does NOT append the % sign (callers add it).
 * Returns '' for null/blank/NaN so callers can fall back to a dash.
 * @param {number|string} value - rate value (e.g. 1.5 for 1.5%)
 * @param {number} maxDecimals - maximum decimals to show (default 4)
 * @returns {string}
 */
export const formatRatePct = (value, maxDecimals = 4) => {
  if (value === null || value === undefined || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  // toFixed then strip trailing zeros (and a dangling decimal point).
  return num.toFixed(maxDecimals).replace(/\.?0+$/, '');
};

/**
 * T3-084: single presentation of a contract's broker fee, so every surface (wizard Review,
 * wizard Summary, Contract Detail, Approval modal) reads identically.
 *
 * Three shapes:
 *  - No-VAT treatment  -> `1.5% — SZL 60 000.00` + a separate "No-VAT" tag. There is only ONE
 *    figure, so printing "excl = incl" twice is noise and an "(incl. VAT)" label is misleading.
 *  - VAT present       -> `1.5% — SZL 60 000.00 excl + SZL 9 000.00 VAT (15%) = SZL 69 000.00 incl`
 *  - VAT zero/absent   -> the exclusive figure only (the pre-T3-084 behaviour, since the
 *    existing `vatAmt ? ... : ''` guards already omitted the VAT clause).
 *
 * Prefers the snapshot columns; falls back to recomputing the exclusive (capital x %) with
 * 0% VAT for legacy contracts saved before broker-fee VAT existed.
 *
 * @param {object} contract - contract row (snapshot columns + capital_amount/broker_fees)
 * @param {string} currencyCode - currency code to render amounts in
 * @returns {{label: string, value: string, isNoVat: boolean}} label/value for a summary row
 */
export const formatBrokerFee = (contract, currencyCode) => {
  const c = contract || {};
  const ccy = currencyCode || c.currency_code || '';
  const isNoVat = c.broker_fee_vat_treatment === 'NO_VAT';

  const excl = c.broker_fee_exclusive_amount != null
    ? parseFloat(c.broker_fee_exclusive_amount)
    : ((parseFloat(c.capital_amount) || 0) * (parseFloat(c.broker_fees) || 0)) / 100;
  const vatPct = c.broker_fee_vat_percent != null ? parseFloat(c.broker_fee_vat_percent) : null;
  const vatAmt = c.broker_fee_vat_amount != null ? parseFloat(c.broker_fee_vat_amount) : 0;
  const incl = c.broker_fee_inclusive_amount != null
    ? parseFloat(c.broker_fee_inclusive_amount)
    : excl + vatAmt;

  const pct = formatRatePct(c.broker_fees);

  if (isNoVat) {
    // One figure only — the "No-VAT" tag carries the reason, so no "incl."/"excl." suffix.
    return { label: 'Broker Fees', value: `${pct}% — ${ccy} ${formatNumber(excl)}`, isNoVat: true };
  }
  if (!vatAmt) {
    // No VAT rate configured for the country: exclusive only (unchanged behaviour).
    return { label: 'Broker Fees', value: `${pct}% — ${ccy} ${formatNumber(excl)}`, isNoVat: false };
  }
  return {
    label: 'Broker Fees (incl. VAT)',
    value: `${pct}% — ${ccy} ${formatNumber(excl)} excl + ${ccy} ${formatNumber(vatAmt)} VAT${vatPct != null ? ` (${formatRatePct(vatPct)}%)` : ''} = ${ccy} ${formatNumber(incl)} incl`,
    isNoVat: false,
  };
};

/**
 * Format a file size in bytes to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);

  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
};

/**
 * Format a phone number
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhone = (phone) => {
  if (!phone) return '-';

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Format South African numbers
  if (cleaned.length === 10 && cleaned.startsWith('0')) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }

  // Format international numbers
  if (cleaned.length > 10) {
    return `+${cleaned.slice(0, -9)} ${cleaned.slice(-9, -6)} ${cleaned.slice(-6, -3)} ${cleaned.slice(-3)}`;
  }

  return phone;
};

/**
 * Truncate text to a specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text with ellipsis if needed
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Format a reference number with leading zeros
 * @param {number} num - Number to format
 * @param {number} length - Desired length
 * @returns {string} Formatted reference with leading zeros
 */
export const formatReference = (num, length = 6) => {
  return String(num).padStart(length, '0');
};

export default {
  formatDate,
  formatDateTime,
  formatCurrency,
  formatNumber,
  formatPercent,
  formatRatePct,
  formatBrokerFee,
  formatFileSize,
  formatPhone,
  truncateText,
  formatReference,
};
